/**
 * FillBlankQuiz Student Renderer
 * Student-style UI matching legacy quebon-tv-web Blank.jsx styling.
 */

import { useMemo, type ReactNode } from 'react'
import { cn } from '../../../lib/utils'
import { SubmitButton, QuizSolveDescription, QuizHeader, QuizResultToggle, type QuizResultToggleValue } from '../shared'
import { formatProblem } from '../../../utils/quiz-parser'
import type { FillBlankQuizData, QuizSolveResult } from '../../../types/legacyQuiz'
import { useFillBlankQuiz } from '../hooks'
import { useTranslation } from 'react-i18next'
import { useQuizStyleVariant } from '../shared/quiz-style-context'
import { QUIZ_INTERACTIVE_STYLES } from '../shared/quiz-styles'
import { AiqKeypad } from '../shared/aiq-keypad'

const BLANK_INPUT_MIN_WIDTH_PX = 48
const BLANK_INPUT_NARROW_CHARACTER_WIDTH_PX = 16
const BLANK_INPUT_WIDE_CHARACTER_WIDTH_PX = 24
const BLANK_INPUT_HORIZONTAL_PADDING_PX = 32
const BLANK_INPUT_BORDER_WIDTH_PX = 4
const AIQ_BLANK_INPUT_MIN_WIDTH_PX = 40
const AIQ_BLANK_INPUT_CHARACTER_WIDTH_PX = 16
const AIQ_BLANK_INPUT_HORIZONTAL_PADDING_PX = 16

function isWideCharacter(character: string): boolean {
  const codePoint = character.codePointAt(0) ?? 0
  return (
    (codePoint >= 0x1100 && codePoint <= 0x115f) ||
    (codePoint >= 0x2e80 && codePoint <= 0xa4cf) ||
    (codePoint >= 0xac00 && codePoint <= 0xd7a3) ||
    (codePoint >= 0xf900 && codePoint <= 0xfaff) ||
    (codePoint >= 0xfe10 && codePoint <= 0xfe6f) ||
    (codePoint >= 0xff01 && codePoint <= 0xff60) ||
    (codePoint >= 0xffe0 && codePoint <= 0xffe6) ||
    (codePoint >= 0x1f300 && codePoint <= 0x1faff) ||
    (codePoint >= 0x20000 && codePoint <= 0x3fffd)
  )
}

function getBlankInputWidth(value: string): string {
  const contentWidth = Array.from(value).reduce(
    (width, character) =>
      width + (
        isWideCharacter(character)
          ? BLANK_INPUT_WIDE_CHARACTER_WIDTH_PX
          : BLANK_INPUT_NARROW_CHARACTER_WIDTH_PX
      ),
    0,
  )
  return `${Math.max(
    BLANK_INPUT_MIN_WIDTH_PX,
    contentWidth + BLANK_INPUT_HORIZONTAL_PADDING_PX + BLANK_INPUT_BORDER_WIDTH_PX,
  )}px`
}

function getLcBlankInputWidth(value: string): string {
  if (value.length === 0) return '40px'
  const contentWidth = Array.from(value).reduce(
    (width, character) =>
      width + (
        isWideCharacter(character)
          ? BLANK_INPUT_WIDE_CHARACTER_WIDTH_PX
          : BLANK_INPUT_NARROW_CHARACTER_WIDTH_PX
      ),
    0,
  )
  return `${Math.max(
    80,
    contentWidth + BLANK_INPUT_HORIZONTAL_PADDING_PX + BLANK_INPUT_BORDER_WIDTH_PX,
  )}px`
}

function getAiqBlankInputWidth(value: string): string {
  const contentWidth = Array.from(value).length * AIQ_BLANK_INPUT_CHARACTER_WIDTH_PX
  return `${Math.max(
    AIQ_BLANK_INPUT_MIN_WIDTH_PX,
    contentWidth + AIQ_BLANK_INPUT_HORIZONTAL_PADDING_PX,
  )}px`
}

interface FillBlankStudentProps {
  quizId: string | number
  quiz: FillBlankQuizData
  onSolve?: (quizId: string | number, answers: string[], vars: Record<string, unknown>, elapsedTime: number) => void
  solveResult?: QuizSolveResult | null
  showReallyTrue?: boolean
  isViewSolved?: boolean
  useKeypad?: boolean
  initialAnswers?: string[]
  submitLabel?: string
  hideSubmitButton?: boolean
  onAnswerChange?: (answers: string[]) => void
  disableSound?: boolean
  difficultyLevel?: number | null
  /** G3 — true면 풀이 후 해설(QuizSolveDescription)을 숨긴다(정오답 피드백은 유지). 기본 false=현행 노출. */
  hideExplanation?: boolean
  /** true면 결과 토글은 유지하되 풀이/해설 UI만 숨긴다. */
  hideSolveDescription?: boolean
  /** G4 — true면 유형별 노출표에 따라 발문/내용/문제 이미지를 흐린다. */
  dimChoiceText?: boolean
}

export function FillBlankStudent({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  useKeypad = false,
  initialAnswers: initialAnswersProp,
  submitLabel,
  hideSubmitButton = false,
  onAnswerChange,
  disableSound: _disableSound = false,
  difficultyLevel,
  hideExplanation = false,
  hideSolveDescription = false,
  dimChoiceText = false,
}: FillBlankStudentProps) {
  const { t } = useTranslation('quiz')
  const variant = useQuizStyleVariant()
  const isLcV3 = variant === 'lc'
  const isAiq = variant === 'aiq'
  const ix = QUIZ_INTERACTIVE_STYLES[variant]
  const keypadActive = useKeypad || variant === 'aiq'

  const {
    answers,
    currentIndex,
    blankCount,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    handleBlankClick,
    handleInputChange,
    handleSubmit,
    handleKeypadInput,
  } = useFillBlankQuiz({
    quizId,
    quiz,
    onSolve,
    solveResult,
    showReallyTrue,
    isViewSolved,
    initialAnswers: initialAnswersProp,
    onAnswerChange,
  })

  // 문제 렌더링 - 레거시 Blank.jsx 스타일 인라인 요소 생성
  const renderedProblem = useMemo<ReactNode[]>(() => {
    if (blankCount === 0) return []

    return formatProblem(
      quiz.problem,
      `${quizId}-problem`,
      (index: number, content: string) => {
        const key = content || ''
        const value = answers[index] ?? ''
        const hasValue = value.length > 0
        const isActive = currentIndex === index
        const isCorrect = isSolved ? (solveResult?.corrects?.[index] ?? null) : null
        const solved = isSolved || isViewSolved

        if (solved) {
          // 풀린 상태: 정답/오답 span
          const correctAnswer = solveResult?.correctAnswers?.[index] ?? ''
          const showCorrectAnswer = shouldShowCorrectAnswers && correctAnswer !== ''
          const displayValue = showCorrectAnswer
            ? correctAnswer
            : (value || '?')

          return (
            <span
              key={`blank-${quizId}-${index}`}
              className={cn(
                'inline-flex items-center justify-center align-middle font-medium transition-all text-center [box-shadow:unset] pointer-events-none',
                isLcV3
                  ? 'mx-1 h-10 rounded-[10px] border-[1.5px] px-2 text-[20px]'
                  : isAiq
                    ? 'mx-1.5 h-11 min-w-10 rounded-[10px] border-[1.5px] px-2 text-[22px] font-bold'
                    : 'mx-2 h-12 rounded-2xl border-none px-4 text-[24px]',
                showCorrectAnswer && (isLcV3
                  ? 'border-lc-success-500 bg-lc-success-50 text-lc-text-success'
                  : 'bg-lc-mint-200 text-lc-mint-900'),
                !showCorrectAnswer && isCorrect === true && (isLcV3
                  ? 'border-lc-success-500 bg-lc-success-50 text-lc-text-success'
                  : 'bg-lc-mint-200 text-lc-mint-900'),
                !showCorrectAnswer && isCorrect === false && (isLcV3
                  ? 'border-lc-error-500 bg-lc-error-50 text-lc-text-error'
                  : 'bg-lc-red-200 text-lc-red-900'),
                !showCorrectAnswer && isCorrect === null && (isLcV3
                  ? 'border-lc-key-500 bg-[#ddf3ff] text-lc-text-primary'
                  : 'bg-lc-lightblue-200 text-gray-900'),
              )}
              style={
                isLcV3
                  ? { width: getLcBlankInputWidth(displayValue) }
                  : isAiq
                    ? { width: getAiqBlankInputWidth(displayValue) }
                    : undefined
              }
            >
              {displayValue}
            </span>
          )
        }

        // 미풀린 상태
        // 키패드 모드(aiq): 시스템 키보드 차단을 위해 input 대신 클릭 가능한 span을 렌더.
        if (keypadActive) {
          return (
            <span
              key={`blank-${quizId}-${index}`}
              data-blank={index}
              onClick={() => handleBlankClick(index)}
              className={cn(
                'inline-flex items-center justify-center align-middle font-medium text-gray-900 transition-all text-center cursor-pointer select-none',
                isLcV3
                  ? 'mx-1 h-10 rounded-[10px] border-[1.5px] px-2 text-[20px]'
                  : isAiq
                    ? 'mx-1.5 h-11 min-w-10 rounded-[10px] border-[1.5px] px-2 text-[22px] font-bold'
                    : 'mx-2 h-12 min-w-[56px] rounded-2xl px-4 text-[24px]',
                !isActive && !hasValue && ix.blankEmpty,
                isActive && ix.blankActive,
                isActive && hasValue && !isLcV3 && !isAiq && '[box-shadow:0px_4px_0px_rgba(85,195,255,0.3)]',
                !isActive && hasValue && ix.blankFilled,
              )}
              style={
                isLcV3
                  ? { width: getLcBlankInputWidth(value) }
                  : isAiq
                    ? { width: getAiqBlankInputWidth(value) }
                    : undefined
              }
            >
              {value || '?'}
            </span>
          )
        }
        return (
          <input
            key={`blank-${quizId}-${index}`}
            type="text"
            value={value}
            onChange={(e) => handleInputChange(index, e.target.value, key)}
            onClick={() => handleBlankClick(index)}
            placeholder="?"
            className={cn(
              'inline-flex items-center justify-center align-middle font-medium text-gray-900 transition-all text-center outline-hidden',
              isLcV3
                ? 'mx-1 h-10 rounded-[10px] border-[1.5px] px-2 text-[20px]'
                : 'mx-2 h-12 rounded-2xl px-4 text-[24px]',
              !isActive && !hasValue && ix.blankEmpty,
              isActive && ix.blankActive,
              isActive && hasValue && !isLcV3 && !isAiq && '[box-shadow:0px_4px_0px_rgba(85,195,255,0.3)]',
              !isActive && hasValue && ix.blankFilled,
              'min-w-0',
            )}
            style={{ width: isLcV3 ? getLcBlankInputWidth(value) : getBlankInputWidth(value) }}
          />
        )
      },
      quiz.additionalImages ?? undefined
    )
  }, [blankCount, currentIndex, isSolved, isViewSolved, solveResult, shouldShowCorrectAnswers, quiz.problem, quizId, handleBlankClick, handleInputChange, answers, quiz.additionalImages, keypadActive, ix, isLcV3, isAiq])

  return (
    <div className={cn('space-y-6', keypadActive && !isSolved && !isViewSolved && 'pb-[304px]')}>
      {/* 문제 헤더 */}
      <QuizHeader
        quizId={quizId}
        typeLabel={t('types.fillBlank.label')}
        title={quiz.title}
        difficultyLevel={difficultyLevel}
        variant="student"
        image={quiz.image}
        imageMobile={quiz.imageMobile}
        desmos={quiz.desmos}
        vars={quiz.vars}
        dimTitleText={dimChoiceText}
        dimMedia={dimChoiceText}
      />

      {/* 문제 본문 - 레거시 스타일: 컨테이너 없이 인라인 렌더링 */}
      <div
        data-quiz-problem-text
        className={cn(
          isLcV3
            ? 'text-[20px] font-medium leading-[1.45] tracking-[-0.1px] text-lc-text-primary'
            : isAiq
              ? 'text-[22px] font-medium leading-normal text-lc-text-primary'
              : 'text-2xl font-medium text-gray-900',
          dimChoiceText && '[&_.formatProblem]:opacity-[0.05] [&_.formatProblem_other]:opacity-[0.05]'
        )}
      >
        {renderedProblem}
      </div>

      {/* 정답 확인 버튼 (키패드 모드에서는 하단 흐름으로 제출하므로 인-플로우 버튼 숨김 — 키패드와 겹침 방지) */}
      {!isSolved && !isViewSolved && !hideSubmitButton && !keypadActive && (
        <SubmitButton
          onClick={handleSubmit}
          disabled={!canSubmit}
          label={submitLabel}
        />
      )}

      {/* 풀이 결과 토글 (정답과 풀이 / 내가 쓴 답) */}
      {isSolved && !hideExplanation && (
        <QuizResultToggle
          value={isShowingCorrectAnswers ? 'correct-and-explanation' : 'my-answer'}
          onChange={(next: QuizResultToggleValue) => setIsShowingCorrectAnswers(next === 'correct-and-explanation')}
        />
      )}

      {/* 해설은 '정답과 풀이' 일 때만 노출 */}
      {isSolved && isShowingCorrectAnswers && !hideExplanation && !hideSolveDescription && (
        <QuizSolveDescription
          description={solveResult?.description ?? undefined}
          commentaryImages={quiz.commentaryImages}
          vars={quiz.vars}
          id={`${quizId}-desc`}
          actionVisible={false}
        />
      )}

      {/* aiq 하단 커스텀 키패드 (모바일 시스템 키보드 대체) */}
      {keypadActive && blankCount > 0 && !isSolved && !isViewSolved && (
        <AiqKeypad
          onInput={handleKeypadInput}
          onPrev={() => { if (currentIndex > 0) handleBlankClick(currentIndex - 1) }}
          onNext={() => { if (currentIndex < blankCount - 1) handleBlankClick(currentIndex + 1) }}
          canPrev={currentIndex > 0}
          canNext={currentIndex < blankCount - 1}
        />
      )}
    </div>
  )
}
